import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Users, 
  Link, 
  Plus, 
  Copy, 
  ExternalLink, 
  Trash2, 
  BarChart3,
  Calendar,
  RefreshCw,
  UserPlus
} from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Card from '../common/Card';
import Loading from '../common/Loading';
import ConfirmModal from '../common/ConfirmModal';
import UserAssignmentModal from './UserAssignmentModal';
import UserAssignmentsList from './UserAssignmentsList';
import { shortUrlAPI, registrationLinkAPI } from '../../api/endpoints';
import { useToastContext } from '../../context/ToastContext';

const EventManagementModal = ({ event, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const [activeTab, setActiveTab] = useState('users');
  const [showUserAssignmentModal, setShowUserAssignmentModal] = useState(false);
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [deleteUrlConfirm, setDeleteUrlConfirm] = useState({ open: false, urlId: null });
  const [expiresAt, setExpiresAt] = useState('');

  // Fetch short URLs
  const { data: shortUrlsData, isLoading: shortUrlsLoading } = useQuery({
    queryKey: ['short-urls', event?.id],
    queryFn: () => shortUrlAPI.getAll(event.id),
    enabled: !!event?.id && isOpen && activeTab === 'links'
  });

  // Fetch registration link
  const { data: registrationLinkData } = useQuery({
    queryKey: ['registration-link', event?.id],
    queryFn: () => registrationLinkAPI.getLink(event.id),
    enabled: !!event?.id && isOpen && activeTab === 'links'
  });

  // Create short URL mutation
  const createShortUrlMutation = useMutation({
    mutationFn: (data: { expires_at: string | null }) => shortUrlAPI.create(event.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['short-urls', event.id] });
      toast.success('Short URL created successfully!');
      setExpiresAt('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create short URL');
    }
  });

  // Delete short URL mutation
  const deleteShortUrlMutation = useMutation({
    mutationFn: (shortUrlId: string) => shortUrlAPI.delete(event.id, shortUrlId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['short-urls', event.id] });
      toast.success('Short URL deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete short URL');
    }
  });

  // Reset registration link mutation
  const resetRegistrationLinkMutation = useMutation({
    mutationFn: () => registrationLinkAPI.resetLink(event.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registration-link', event.id] });
      queryClient.invalidateQueries({ queryKey: ['short-urls', event.id] });
      setShowResetConfirmModal(false);
      toast.success('Registration link has been reset. Previous links are now invalid.');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reset registration link');
    }
  });

  const handleResetClick = () => {
    setShowResetConfirmModal(true);
  };

  const confirmReset = () => {
    resetRegistrationLinkMutation.mutate();
  };

  const handleCreateShortUrl = () => {
    createShortUrlMutation.mutate({
      expires_at: expiresAt || null
    });
  };

  const handleCopyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const handleDeleteShortUrl = (shortUrl) => {
    setDeleteUrlConfirm({ open: true, urlId: shortUrl.id });
  };
  
  const confirmDeleteUrl = () => {
    if (deleteUrlConfirm.urlId) {
      deleteShortUrlMutation.mutate(deleteUrlConfirm.urlId);
    }
    setDeleteUrlConfirm({ open: false, urlId: null });
  };

  const tabs = [
    { id: 'users', label: 'User Assignments', icon: Users },
    { id: 'links', label: 'Registration Links', icon: Link }
  ];

  if (!event) return null;

  return (
    <>
      <Modal 
        isOpen={isOpen} 
        onClose={onClose}
        title={`Manage ${event.name}`}
        size="xl"
      >
        <div className="space-y-6">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]">
            {activeTab === 'users' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">User Assignments</h3>
                  <Button
                    onClick={() => setShowUserAssignmentModal(true)}
                    className="gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    Assign User
                  </Button>
                </div>
                <UserAssignmentsList event={event} onEditAssignment={() => {}} />
              </div>
            )}

            {activeTab === 'links' && (
              <div className="space-y-6">
                {/* Registration Link Management */}
                <Card>
                  <Card.Content className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900">Main Registration Link</h4>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleResetClick}
                        loading={resetRegistrationLinkMutation.isPending}
                        className="gap-2 text-orange-600 border-orange-200 hover:bg-orange-50"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Reset Link
                      </Button>
                    </div>
                    
                    {registrationLinkData?.data && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Public Registration Link (Rate Limited)</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={registrationLinkData.data.registration_url}
                              readOnly
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCopyUrl(registrationLinkData.data.registration_url)}
                              className="gap-2"
                            >
                              <Copy className="h-4 w-4" />
                              Copy
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(registrationLinkData.data.registration_url, '_blank')}
                              className="gap-2"
                            >
                              <ExternalLink className="h-4 w-4" />
                              Open
                            </Button>
                          </div>
                        </div>

                        {registrationLinkData.data.usher_url && (
                          <div>
                            <label className="block text-xs font-medium text-purple-600 mb-1">Usher Mode Link (Bypasses Rate Limits - Keep Secret)</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={registrationLinkData.data.usher_url}
                                readOnly
                                className="flex-1 px-3 py-2 border border-purple-200 rounded-lg bg-purple-50 text-sm text-purple-700"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCopyUrl(registrationLinkData.data.usher_url)}
                                className="gap-2 border-purple-200 text-purple-600 hover:bg-purple-50"
                              >
                                <Copy className="h-4 w-4" />
                                Copy
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(registrationLinkData.data.usher_url, '_blank')}
                                className="gap-2 border-purple-200 text-purple-600 hover:bg-purple-50"
                              >
                                <ExternalLink className="h-4 w-4" />
                                Open
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Resetting the link will invalidate all previous registration URLs and short URLs.
                    </p>
                  </Card.Content>
                </Card>

                {/* Short URLs Management */}
                <Card>
                  <Card.Content className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900">Short URLs</h4>
                    </div>

                    {/* Create Short URL */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h5 className="font-medium text-gray-900 mb-3">Create New Short URL</h5>
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Expiration Date (Optional)
                          </label>
                          <input
                            type="datetime-local"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            onClick={handleCreateShortUrl}
                            loading={createShortUrlMutation.isPending}
                            className="gap-2"
                          >
                            <Plus className="h-4 w-4" />
                            Create
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Short URLs List */}
                    {shortUrlsLoading ? (
                      <Loading text="Loading short URLs..." />
                    ) : (
                      <div className="space-y-3">
                        {Array.isArray(shortUrlsData?.data) && shortUrlsData.data.length > 0 ? (
                          shortUrlsData.data.map((shortUrl) => (
                            <div
                              key={shortUrl.id}
                              className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                            >
                              <div className="flex-1">
                                <div className="font-mono text-sm text-blue-600 mb-1">
                                  {shortUrl.short_url}
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <BarChart3 className="h-3 w-3" />
                                    {shortUrl.click_count} clicks
                                  </span>
                                  {shortUrl.expires_at && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      Expires {new Date(shortUrl.expires_at).toLocaleDateString()}
                                    </span>
                                  )}
                                  <span className={`px-2 py-1 rounded-full text-xs ${
                                    shortUrl.is_active && !shortUrl.is_expired
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {shortUrl.is_active && !shortUrl.is_expired ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCopyUrl(shortUrl.short_url)}
                                  className="gap-1"
                                >
                                  <Copy className="h-3 w-3" />
                                  Copy
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.open(shortUrl.short_url, '_blank')}
                                  className="gap-1"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  Open
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => handleDeleteShortUrl(shortUrl)}
                                  loading={deleteShortUrlMutation.isPending}
                                  className="gap-1"
                                >
                                  <Trash2 className="h-3 w-3" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Link className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                            <p>No short URLs created yet</p>
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Content>
                </Card>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* User Assignment Modal */}
      <UserAssignmentModal
        event={event}
        isOpen={showUserAssignmentModal}
        onClose={() => setShowUserAssignmentModal(false)}
      />

      {/* Reset Registration Link Confirmation Modal */}
      <ConfirmModal
        isOpen={showResetConfirmModal}
        onClose={() => setShowResetConfirmModal(false)}
        onConfirm={confirmReset}
        title="Reset Registration Link"
        message={
          <div className="space-y-4">
            <p>
              Are you sure you want to reset the registration link for <strong>"{event?.name}"</strong>?
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                What will happen:
              </h4>
              <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                <li>A new registration token will be generated</li>
                <li>All existing registration links will become invalid</li>
                <li>All existing short URLs will become invalid</li>
                <li>QR codes with old links will stop working</li>
                <li>You'll need to share the new registration link</li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">
                When to reset:
              </h4>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li>Registration link has been compromised or shared inappropriately</li>
                <li>You want to stop registrations temporarily and restart with a new link</li>
                <li>Security concerns about who has access to the current link</li>
              </ul>
            </div>

            <p className="text-sm text-gray-600">
              <strong>Note:</strong> This action cannot be undone. Make sure you're ready to distribute new registration links.
            </p>
          </div>
        }
        confirmText="Reset Link"
        cancelText="Cancel"
        variant="warning"
        loading={resetRegistrationLinkMutation.isPending}
      />
      
      {/* Delete URL Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteUrlConfirm.open}
        onClose={() => setDeleteUrlConfirm({ open: false, urlId: null })}
        onConfirm={confirmDeleteUrl}
        title="Delete Short URL"
        message="Are you sure you want to delete this short URL? Users clicking this link will no longer be able to access the registration page."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

export default EventManagementModal;
